<!--
  @component ImportModal
  @description Modal for importing bibliographic records from catalogs or files
-->
<script lang="ts">
  import { createEventDispatcher, onMount } from 'svelte';
  import { t } from '@/i18n';
  import { zoteroService } from '@services/zoteroService';
  import { notify } from '@services/notificationService';
  import { selectedLibraryId, selectedLibraryType } from '@stores/libraryStore';
  import { selectedCollectionKey } from '@stores/collectionStore';
  import { ImportCatalogTab, ImportFileTab } from './import';
  import type { CreateItemInput, UnifiedRecord } from '@types';

  export let visible = false;

  const dispatch = createEventDispatcher<{
    close: void;
    imported: { count: number };
  }>();

  type MainTab = 'catalogs' | 'file';

  let activeMainTab: MainTab = 'catalogs';
  let isImporting = false;
  let selectedRecords: Set<string> = new Set();

  // Component references
  let catalogTab: ImportCatalogTab;
  let fileTab: ImportFileTab;

  $: canImport = selectedRecords.size > 0 && !isImporting;

  function handleMainTabChange(tab: MainTab) {
    if (tab !== activeMainTab) {
      activeMainTab = tab;
      selectedRecords = new Set();
    }
  }

  function handleSelectionChange(event: CustomEvent<{ selectedRecords: Set<string> }>) {
    selectedRecords = event.detail.selectedRecords;
  }

  async function handleImport() {
    if (!$selectedLibraryId || !$selectedLibraryType) {
      notify.error($t('import.noLibrarySelected'));
      return;
    }

    const records =
      activeMainTab === 'catalogs'
        ? catalogTab?.getSearchResults() || []
        : fileTab?.getFileRecords() || [];

    const recordsToImport = records.filter((r: UnifiedRecord) => selectedRecords.has(r.id));
    if (recordsToImport.length === 0) {
      notify.warning($t('import.selectRecordsFirst'));
      return;
    }

    isImporting = true;
    let importedCount = 0;
    let failedCount = 0;

    for (const record of recordsToImport) {
      try {
        const collectionKey =
          $selectedCollectionKey && $selectedCollectionKey !== 'all'
            ? $selectedCollectionKey
            : null;

        const itemData: CreateItemInput = {
          ...record.item,
          collections: collectionKey ? [collectionKey] : [],
        } as CreateItemInput;

        const result = await zoteroService.createItem(
          $selectedLibraryId,
          $selectedLibraryType,
          itemData
        );

        if (result.success) {
          importedCount++;
        } else {
          failedCount++;
        }
      } catch {
        failedCount++;
      }
    }

    isImporting = false;

    if (importedCount > 0) {
      notify.success($t('import.importSuccess', { count: importedCount }));
      dispatch('imported', { count: importedCount });
      handleClose();
    }

    if (failedCount > 0) {
      notify.warning($t('import.importPartialFailed', { count: failedCount }));
    }

    selectedRecords = new Set();
  }

  function handleClose() {
    selectedRecords = new Set();
    activeMainTab = 'catalogs';
    fileTab?.reset();
    dispatch('close');
  }

  function handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      handleClose();
    }
  }

  function handleBackdropClick(event: MouseEvent) {
    if (event.target === event.currentTarget) {
      handleClose();
    }
  }

  onMount(() => {
    document.addEventListener('keydown', handleKeydown);
    return () => {
      document.removeEventListener('keydown', handleKeydown);
    };
  });
</script>

{#if visible}
  <div
    class="modal-backdrop"
    on:click={handleBackdropClick}
    on:keydown={handleKeydown}
    role="presentation"
  >
    <div class="modal">
      <div class="modal-header">
        <h2 id="import-modal-title">{$t('import.title')}</h2>
        <button class="close-button" on:click={handleClose} aria-label={$t('common.close')}>
          &times;
        </button>
      </div>

      <div class="modal-body">
        <!-- Main Tabs -->
        <div class="main-tabs">
          <button
            class="main-tab"
            class:active={activeMainTab === 'catalogs'}
            on:click={() => handleMainTabChange('catalogs')}
          >
            {$t('import.tabCatalogs')}
          </button>
          <button
            class="main-tab"
            class:active={activeMainTab === 'file'}
            on:click={() => handleMainTabChange('file')}
          >
            {$t('import.tabFile')}
          </button>
        </div>

        {#if activeMainTab === 'catalogs'}
          <ImportCatalogTab
            bind:this={catalogTab}
            bind:selectedRecords
            on:selectionChange={handleSelectionChange}
          />
        {:else}
          <ImportFileTab
            bind:this={fileTab}
            bind:selectedRecords
            on:selectionChange={handleSelectionChange}
          />
        {/if}
      </div>

      <div class="modal-footer">
        <button class="btn btn-secondary" on:click={handleClose}>
          {$t('common.cancel')}
        </button>
        <button class="btn btn-primary" on:click={handleImport} disabled={!canImport}>
          {#if isImporting}
            {$t('import.importing')}
          {:else}
            {$t('import.importSelected', { count: selectedRecords.size })}
          {/if}
        </button>
      </div>
    </div>
  </div>
{/if}

<style>
  .modal-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.6);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2000;
    animation: fadeIn 0.15s ease-out;
  }

  .modal {
    background: var(--color-bg-secondary, #383838);
    border-radius: 8px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
    width: 90vw;
    max-width: 900px;
    max-height: 85vh;
    display: flex;
    flex-direction: column;
    animation: slideIn 0.2s ease-out;
  }

  .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
    border-bottom: 1px solid var(--color-border, #555);
  }

  .modal-header h2 {
    margin: 0;
    font-size: 18px;
    color: var(--color-text-primary, #e8e8e8);
  }

  .close-button {
    background: none;
    border: none;
    font-size: 24px;
    color: var(--color-text-secondary, #aaa);
    cursor: pointer;
    padding: 0;
    line-height: 1;
  }

  .close-button:hover {
    color: var(--color-text-primary, #e8e8e8);
  }

  .modal-body {
    padding: 20px;
    overflow-y: auto;
    flex: 1;
  }

  /* Main Tabs */
  .main-tabs {
    display: flex;
    gap: 0;
    margin-bottom: 20px;
    border-bottom: 2px solid var(--color-border, #555);
  }

  .main-tab {
    flex: 1;
    padding: 14px 20px;
    background: transparent;
    border: none;
    border-bottom: 3px solid transparent;
    margin-bottom: -2px;
    color: var(--color-text-secondary, #aaa);
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
  }

  .main-tab:hover {
    color: var(--color-text-primary, #e8e8e8);
    background: var(--color-bg-hover, #444);
  }

  .main-tab.active {
    color: var(--color-accent, #5c7cfa);
    border-bottom-color: var(--color-accent, #5c7cfa);
  }

  /* Footer */
  .modal-footer {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding: 16px 20px;
    border-top: 1px solid var(--color-border, #555);
  }

  /* Buttons */
  .btn {
    padding: 10px 16px;
    border-radius: 4px;
    font-size: 14px;
    cursor: pointer;
    transition: background-color 0.2s;
  }

  .btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .btn-secondary {
    background: var(--color-bg-tertiary, #4a4a4a);
    border: 1px solid var(--color-border, #555);
    color: var(--color-text-primary, #e8e8e8);
  }

  .btn-secondary:hover:not(:disabled) {
    background: var(--color-bg-hover, #555);
  }

  .btn-primary {
    background: var(--color-accent, #5c7cfa);
    border: 1px solid var(--color-accent, #5c7cfa);
    color: white;
  }

  .btn-primary:hover:not(:disabled) {
    background: var(--color-accent-hover, #4c6ef5);
  }

  /* Animations */
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(-20px) scale(0.95);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }

  /* Responsive */
  @media (max-width: 768px) {
    .modal {
      width: 95vw;
      max-height: 90vh;
    }
  }
</style>
